import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

// Response shape of GET /brands/:brandId/search (apps/api/src/routes/search.ts)

export interface SearchPiece {
  id: string;
  shortCode: string;
  snippet: string;
  platform: string;
  status: string;
  projectId: string | null;
}

export interface SearchIdea {
  id: string;
  title: string;
  status: string;
}

export interface SearchConversation {
  id: string;
  participantName: string | null;
  platform: string;
  lastMessageText: string;
  lastMessageAt: string;
}

export interface SearchResults {
  pieces: SearchPiece[];
  ideas: SearchIdea[];
  conversations: SearchConversation[];
}

export const searchApi = {
  search: (brandId: string, q: string) =>
    apiFetch<SearchResults>(`/brands/${brandId}/search?q=${encodeURIComponent(q)}`),
};

/** Gating used by useSearch's `enabled` (spec §5: min query length 2). */
export function canSearch(brandId: string | undefined, q: string): boolean {
  return !!brandId && q.trim().length >= 2;
}

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function useSearch(brandId: string | undefined, q: string) {
  const debouncedQ = useDebouncedValue(q, 200);
  const trimmed = debouncedQ.trim();
  return useQuery({
    queryKey: ["search", brandId, trimmed],
    queryFn: () => searchApi.search(brandId!, trimmed),
    enabled: canSearch(brandId, trimmed),
    staleTime: 30_000,
  });
}
